import { useMemo, useState } from "react";
import type { DocAsset } from "@/lib/document/assetTypes";
import type { DocBlock } from "@/lib/document-builder/blocks/blockTypes";
import type { DocVariableSnapshot } from "@/lib/document-builder/versions/snapshotTypes";
import {
  buildInitialVariableValues,
  type DocVariableValues,
} from "@/lib/document-builder/variables/variableFill";
import { mergeVariablesFromBlocks } from "@/lib/document-builder/variables/variableFillFields";
import { DocumentExportPreviewModal } from "../DocumentExportPreviewModal";
import { DocumentFillModal } from "../DocumentFillModal";

type Props = {
  documentId: string;
  documentName: string;
  blocks: DocBlock[];
  variables: DocVariableSnapshot[];
  assets: DocAsset[];
  previewVersionId: string;
  previewVersionNumber: number;
  disabled?: boolean;
};

export function DocumentEditorPdfPreview(props: Props) {
  const {
    documentId,
    documentName,
    blocks,
    variables,
    assets,
    previewVersionId,
    previewVersionNumber,
    disabled = false,
  } = props;

  const [fillModalOpen, setFillModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFillValues, setPreviewFillValues] = useState<DocVariableValues>({});

  const previewVariables = useMemo(
    () => mergeVariablesFromBlocks(blocks, variables),
    [blocks, variables],
  );

  function openFillModal() {
    setPreviewFillValues(buildInitialVariableValues(previewVariables));
    setFillModalOpen(true);
  }

  return (
    <>
      <button
        type="button"
        disabled={disabled || blocks.length === 0}
        onClick={openFillModal}
        className="rounded-md border border-indigo-300 px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
      >
        Preview PDF
      </button>

      <DocumentFillModal
        open={fillModalOpen}
        documentName={documentName}
        variables={previewVariables}
        blocks={blocks}
        initialValues={previewFillValues}
        requireFilledFields={false}
        proceedLabel="Preview PDF"
        onClose={() => setFillModalOpen(false)}
        onProceed={(values) => {
          setPreviewFillValues(values);
          setFillModalOpen(false);
          setPreviewOpen(true);
        }}
      />
      <DocumentExportPreviewModal
        open={previewOpen}
        documentId={documentId}
        versionId={previewVersionId}
        documentName={documentName}
        versionNumber={previewVersionNumber}
        blocks={blocks}
        assets={assets}
        filledValues={previewFillValues}
        skipPreviewGate
        allowRegisterExport={false}
        previewTitle="PDF preview"
        previewHint="Empty fill fields render as blank lines for manual completion. Download or print from here."
        onClose={() => setPreviewOpen(false)}
      />
    </>
  );
}
